import { prisma } from "@marble/db/client";
import { CareSponsorType, State } from "@prisma/client";

import {
  CareSponsorshipEndReason,
  type CareSponsorshipEndReason as CareSponsorshipEndReasonType,
} from "@marble/core/modules/care-sponsorship/constants";

/**
 * Creates a CareSponsorship and sets `Student.activeCareSponsorshipId` in
 * the same transaction (same pointer pattern as `activeInsuranceInfoId`).
 *
 * Locks the student row (`SELECT … FOR UPDATE`) so concurrent creates for
 * the same student serialize. Idempotent: if the student already points at
 * a real, still-open CareSponsorship, returns that row instead of throwing
 * or creating a duplicate. Orphaned or ended pointers are cleared first so
 * the create always leaves a consistent id ↔ relation pair.
 *
 * Client status transitions are handled by app-side callers.
 */
export const createCareSponsorship = async ({
  studentId,
  careSponsorId,
  sessionCap,
  createdByUserId,
}: {
  studentId: number;
  careSponsorId: number;
  sessionCap?: number | null;
  createdByUserId?: number | null;
}) => {
  const careSponsor = await prisma.careSponsor.findUnique({
    where: { id: careSponsorId },
  });

  if (!careSponsor) {
    throw new Error("Care sponsor not found");
  }

  if (!careSponsor.active) {
    throw new Error("Care sponsor selected is not active");
  }

  return prisma.$transaction(async (tx) => {
    const [locked] = await tx.$queryRaw<
      { id: number; activeCareSponsorshipId: number | null }[]
    >`SELECT id, "activeCareSponsorshipId" FROM "Student" WHERE id = ${studentId} FOR UPDATE`;

    if (!locked) {
      throw new Error("Student not found");
    }

    if (locked.activeCareSponsorshipId) {
      const existing = await tx.careSponsorship.findUnique({
        where: { id: locked.activeCareSponsorshipId },
      });
      if (existing && existing.endedAt == null) {
        return existing;
      }
      // Orphaned or ended pointer — clear so the create below attaches a
      // real open CareSponsorship.
      await tx.student.update({
        where: { id: studentId },
        data: { activeCareSponsorshipId: null },
      });
    }

    const careSponsorship = await tx.careSponsorship.create({
      data: {
        studentId,
        careSponsorId,
        sessionCap: sessionCap ?? careSponsor.defaultSessionCap ?? null,
        createdByUserId: createdByUserId ?? null,
      },
    });

    await tx.student.update({
      where: { id: studentId },
      data: { activeCareSponsorshipId: careSponsorship.id },
    });

    return careSponsorship;
  });
};

/**
 * Creates an INSURANCE_PAYER_NETWORK_STATUS CareSponsor from the admin Care
 * Sponsors tab's "Add Care Sponsor" flow. One write per creatable
 * CareSponsorType, since each sets a different eligibility-resolving field —
 * see `createMarbleStateUninsuredCareSponsor` below for the state variant.
 * `SCHOOL`/`SCHOOL_NETWORK` sponsors still have no creation UI.
 *
 * `careSponsorType`, `insurancePayerNetworkStatusId`, and `defaultSessionCap`
 * are set once here and never editable afterward — see `updateCareSponsor`
 * below for why `defaultSessionCap` in particular stays locked.
 */
export const createInsurancePayerNetworkStatusCareSponsor = async ({
  name,
  description,
  defaultSessionCap,
  active,
  insurancePayerNetworkStatusId,
}: {
  name: string;
  description?: string | null;
  defaultSessionCap?: number | null;
  active: boolean;
  insurancePayerNetworkStatusId: number;
}) => {
  return prisma.careSponsor.create({
    data: {
      name,
      careSponsorType: CareSponsorType.INSURANCE_PAYER_NETWORK_STATUS,
      description: description ?? null,
      defaultSessionCap: defaultSessionCap ?? null,
      active,
      insurancePayerNetworkStatus: insurancePayerNetworkStatusId,
    },
  });
};

/**
 * Creates a MARBLE_STATE_UNINSURED_PATIENTS CareSponsor from the admin Care
 * Sponsors tab's "Add Care Sponsor" flow — Marble sponsoring care for
 * uninsured patients in a single state.
 *
 * `careSponsorType`, `state`, and `defaultSessionCap` are set once here and
 * never editable afterward — see `updateCareSponsor` below for why
 * `defaultSessionCap` in particular stays locked. `state` is unique on
 * `CareSponsor`, so callers should filter their options through
 * `getSelectableStatesForUninsuredCareSponsor` rather than let the DB
 * constraint be the error path.
 */
export const createMarbleStateUninsuredCareSponsor = async ({
  name,
  description,
  defaultSessionCap,
  active,
  state,
}: {
  name: string;
  description?: string | null;
  defaultSessionCap?: number | null;
  active: boolean;
  state: State;
}) => {
  return prisma.careSponsor.create({
    data: {
      name,
      careSponsorType: CareSponsorType.MARBLE_STATE_UNINSURED_PATIENTS,
      description: description ?? null,
      defaultSessionCap: defaultSessionCap ?? null,
      active,
      state,
    },
  });
};

/**
 * Updates the admin-editable fields on a CareSponsor: `name`, `description`,
 * and `active`. Deliberately does NOT accept `defaultSessionCap` —
 * `CareSponsorship.sessionCap` is snapshotted from it at enrollment, but any
 * currently-open sponsorship enrolled while `defaultSessionCap` was `null`
 * still resolves the cap live from this field on every read (see
 * `getCareSponsorshipSessionUsage`). Tightening the default after the fact
 * can retroactively cap — and auto-end via the `endForCapReached` cron — a
 * previously-unlimited, still-active sponsorship. Changing it safely needs a
 * guard (or a backfill of open `sessionCap: null` rows) that doesn't exist
 * yet, so it's left out of this write until that's built.
 */
export const updateCareSponsor = async ({
  id,
  name,
  description,
  active,
}: {
  id: number;
  name: string;
  description?: string | null;
  active: boolean;
}) => {
  return prisma.careSponsor.update({
    where: { id },
    data: { name, description, active },
  });
};

/**
 * Stamps `endingSoonNotifiedAt` on a CareSponsorship once the cron has fired
 * the "ending soon" heads-up, so subsequent daily runs can skip it if the
 * appointment has not been rescheduled since.
 */
export const markCareSponsorshipEndingSoonNotified = async (
  careSponsorshipId: number,
) => {
  return prisma.careSponsorship.update({
    where: { id: careSponsorshipId },
    data: { endingSoonNotifiedAt: new Date() },
  });
};

/**
 * Upcoming billed-client rows still stamped with this sponsorship. Used to
 * move those sessions off sponsor billing when we SWITCHED_TO_INSURANCE —
 * claims key off `UserAppointment.careSponsorshipId`, not the ended
 * CareSponsorship row.
 */
export const upcomingAppointmentsStampedWithCareSponsorshipWhere = (
  careSponsorshipId: number,
  now: Date = new Date(),
) => ({
  careSponsorshipId,
  cancelledAt: null,
  Appointment: {
    startTime: { gt: now },
    cancelledAt: null,
  },
});

/**
 * Clears `careSponsorshipId` on future uncancelled UserAppointments for this
 * sponsorship so they bill insurance instead of the sponsor. Past sessions
 * keep their stamp. Idempotent.
 */
export const clearCareSponsorshipStampsOnUpcomingAppointments = async (
  careSponsorshipId: number,
  db: {
    userAppointment: {
      updateMany: (typeof prisma)["userAppointment"]["updateMany"];
    };
  } = prisma,
) => {
  return db.userAppointment.updateMany({
    where:
      upcomingAppointmentsStampedWithCareSponsorshipWhere(careSponsorshipId),
    data: { careSponsorshipId: null },
  });
};

/**
 * Ends the student's active CareSponsorship and clears
 * the `Student.activeCareSponsorshipId` pointer. Returns `null` if the
 * student has no active sponsorship.
 *
 * For `SWITCHED_TO_INSURANCE`, also clears `careSponsorshipId` on future
 * (not-yet-started, uncancelled) UserAppointments stamped with this
 * sponsorship so claims/billing switch to insurance. Past sessions keep
 * their stamp as a permanent record.
 *
 * Client status transitions and appointment cancellation (for other end
 * reasons) are handled by app-side callers (see
 * `endCareSponsorshipForStudent`).
 */
export const endCareSponsorship = async ({
  studentId,
  reason,
}: {
  studentId: number;
  reason: CareSponsorshipEndReasonType;
}) => {
  const student = await prisma.student.findUnique({
    where: { id: studentId },
    select: { activeCareSponsorshipId: true },
  });

  if (!student?.activeCareSponsorshipId) {
    return null;
  }

  const careSponsorshipId = student.activeCareSponsorshipId;

  return prisma.$transaction(async (tx) => {
    // CareSponsor comes back with the row so callers can branch on
    // `careSponsorType` for their follow-up side effects (e.g. the ended
    // Slack post, which is school-sponsorship-only) without a second lookup
    // against a sponsorship they've just detached from the student.
    const careSponsorship = await tx.careSponsorship.update({
      where: { id: careSponsorshipId },
      data: { endedAt: new Date(), offboardedReason: reason },
      include: { CareSponsor: true },
    });

    await tx.student.update({
      where: { id: studentId },
      data: { activeCareSponsorshipId: null },
    });

    if (reason === CareSponsorshipEndReason.SWITCHED_TO_INSURANCE) {
      await clearCareSponsorshipStampsOnUpcomingAppointments(
        careSponsorshipId,
        tx,
      );
    }

    return careSponsorship;
  });
};
